import { forwardRef, useState, useEffect, useRef } from 'react'

import { styled } from 'styled-components'

import { withFormValue, withLabel } from 'peach/decorators'
import { humanizeKey } from 'peach/helpers'

import RawTextarea from '../Textarea/RawTextarea'

const JsonInput = forwardRef((props, ref) => {
  const { value, onChange, formKey, placeholder, minRows, nullable, ...rest } =
    props

  const [jsonString, setJsonString] = useState(() => {
    if (value === null || value === undefined) return ''
    try {
      return JSON.stringify(value, null, 2)
    } catch {
      return ''
    }
  })
  const [error, setError] = useState('')
  const isUserTypingRef = useRef(false)

  // Update local state when external value changes (but not when user is typing)
  useEffect(() => {
    if (isUserTypingRef.current) return

    try {
      if (value === null || value === undefined) {
        setJsonString('')
        setError('')
        return
      }
      const newJsonString = JSON.stringify(value, null, 2)
      setJsonString(newJsonString)
      setError('')
    } catch {
      // Keep current string if new value can't be stringified
    }
  }, [value])

  const handleChange = (newJsonString) => {
    isUserTypingRef.current = true
    setJsonString(newJsonString)

    // Handle empty/nullable values
    if (nullable && (newJsonString === '' || newJsonString.trim() === '')) {
      setError('')
      onChange?.(null)
      setTimeout(() => {
        isUserTypingRef.current = false
      }, 100)
      return
    }

    try {
      const parsed = JSON.parse(newJsonString)
      setError('')
      onChange?.(parsed)
    } catch (parseError) {
      setError(
        `Invalid JSON: ${
          parseError instanceof Error ? parseError.message : 'Unknown error'
        }`,
      )
    }

    // Reset typing flag after a short delay
    setTimeout(() => {
      isUserTypingRef.current = false
    }, 100)
  }

  const displayPlaceholder = placeholder || humanizeKey(formKey)

  return (
    <Wrapper>
      <RawTextarea
        {...rest}
        minRows={minRows ?? 6}
        onChange={handleChange}
        placeholder={displayPlaceholder}
        ref={ref}
        value={jsonString}
      />
      {error && <ErrorText>{error}</ErrorText>}
      {!error && jsonString && jsonString.trim() && (
        <SuccessText>✅ Valid JSON</SuccessText>
      )}
    </Wrapper>
  )
})

JsonInput.displayName = 'JsonInput'

export default withFormValue(withLabel(JsonInput))

const Wrapper = styled.div`
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
`

const ErrorText = styled.div`
  margin-top: 4px;
  color: ${(p) => p.theme.error || 'red'};
  font-size: 12px;
`

const SuccessText = styled.div`
  margin-top: 4px;
  color: ${(p) => p.theme.success || 'green'};
  font-size: 12px;
`
